import React from 'react';
import PropTypes from 'prop-types';

import { 
  default as useLocalStorageState,
}                         from 'use-local-storage-state';

import {
  FieldValue
} from '../../../packages/detail-panel';

import {
  ResourceCard,
  ResourceRef,
} from '../../resource';

import { 
  fmtBigInteger,
  fmtBytes,
  fmtDaysHrsMinFromSecs,
  fmtIso8601AsLocalDateTime,
  fmtS3ConsoleLink,
} from '../../utils/formatters';

import { 
  BucketIcon,
} from '../../aws';

import {
  PlayArrow as StartCrawlIcon,
  Stop as StopCrawlIcon,
} from '@material-ui/icons';

import { 
  CrawlStatus,
  DatasetActionStartCrawl,
  DatasetActionStopCrawl,
  DatasetTables,
} from '.';

const fmtCrawlMessage = (crawl_data) => {
  return crawl_data?.ErrorMessage || crawl_data?.Message || "-" ; 
};

const handleGoToBucket = (bucket) => {
  window.open(fmtS3ConsoleLink(bucket),"_blank");
};

export default function DatasetCard(props) {

  const { resource } = props;
  const [openCrawlManager, setOpenCrawlManager] = React.useState(false);

  const [expandSchema, doExpandSchema] = React.useState(0);
  const [schemaExpanded, setSchemaExpanded] = useLocalStorageState("dataset_se", false);

  if(!resource) return(null); 

  const handleExpandClick = (value) => {
    setSchemaExpanded(value);
  };

  const actions=[];
  let crawlAction=null;

  if(resource.Location?.S3Uri) {
    actions.push({ 
      name: `Go to Bucket...`,
      icon: BucketIcon,
      onClick: () => handleGoToBucket(resource.Location?.S3Uri),
    });
  }
  
  if(resource.CatalogConfiguration?.Status==='ENABLED') {
    crawlAction=resource.Metrics?.Crawl?.Latest?.Data?.Status==='RUNNING'?'Stop':'Start';
    actions.push({ 
      name: `${crawlAction} Crawl...`,
      icon: crawlAction==='Start'?StartCrawlIcon:StopCrawlIcon,
      onClick: () => setOpenCrawlManager(true),
    });
  }

  const fields=[
    { section: "Details", fields: [
      { name: "ResourceId", label: "Dataset ID", component: FieldValue, options: { maxLength: 40 } }, 
      { name: "ClientAccountId", label: "Client", component: ResourceRef, options: { type: 'Client' } },
      { name: "Location.S3Uri", label: "Location", component: FieldValue, options: { maxLength: 40 } },
      { name: "Location.ResourceId", label: "Target Resource", component: ResourceRef, 
        options: { 
          type: resource.Location.ResourceType, 
          format: 'qualified',
          adorned: true,
          linked: true,
        }
      },
      // { name: "Location.ResourceType", label: "Location Type", formatter: fmtTitleCase },
      { name: "StackCreated", label: "Created", formatter: fmtIso8601AsLocalDateTime },
      { name: "StackUpdated", label: "Updated", formatter: fmtIso8601AsLocalDateTime },
      { name: "ResourceVersion", label: "Version" },
      { name: "ResourceMigration", label: "Migration" },
    ]},
    { section: "Catalog", fields: [
      { name: "CatalogConfiguration.Status", label: "Status" },
      { name: "CatalogConfiguration.DatabaseName", label: "Database" },
      { name: "CatalogConfiguration.Cooldown", label: "Cooldown" },
      { name: "CatalogConfiguration.TableStrategy", label: "Table Strategy" },
      { name: "CatalogConfiguration.ExclusionGlobs", label: "Exclusions" },
      { name: "CatalogConfiguration.Schema.PartitionAddOrUpdateBehavior", label: "Partitions" },
      { name: "CatalogConfiguration.Schema.UpdateBehavior", label: "Update Behavior" },
      { name: "CatalogConfiguration.Schema.DeleteBehavior", label: "Delete Behavior" },
    ]},
    { section: "Security", fields: [
      { name: "SecurityConfiguration.SharingStatus", label: "Sharing" },
      { name: "SecurityConfiguration.AuthorizedReadOnlyAccountIds", label: "Readers", labelAlign: "baseline", component:ResourceRef, 
        options: { type: 'Client', format: 'multiline', sorted: true } },
    ]},
    { section: "Compaction", fields: [
      { name: "CompactionConfiguration.Status", label: "Status" },
      { name: "CompactionConfiguration.CooldownDays", label: "Cooldown" },
      { name: "CompactionConfiguration.DesiredObjectSizeMBs", label: "Object Size" },
      { name: "CompactionConfiguration.IntervalDays", label: "Interval" },
      { name: "CompactionConfiguration.PartitionAge", label: "Partition Age" },
      { name: "CompactionConfiguration.OutputCodec", label: "Compression" },
    ]},
    { section: "Metrics", fields: [
      { name: "Metrics.Inventory.Latest.Data.TotalObjectBytes", label: "Size", formatter: fmtBytes },
      { name: "Metrics.Inventory.Latest.Data.TotalObjects", label: "Objects", formatter: fmtBigInteger },
      { name: "Metrics.CloudWatch.Latest.Data.BytesUploaded.Value", label: "Up/day", formatter: fmtBytes },
      { name: "Metrics.CloudWatch.Latest.Data.BytesDownloaded.Value", label: "Down/day", formatter: fmtBytes },
      { name: "Metrics.CloudWatch.Latest.Data.Requests.Value", label: "Requests/day", formatter: fmtBigInteger },
    ]},    
  ];
  
  if(resource?.CatalogConfiguration?.Status === 'ENABLED') {
    fields.push({ section: "Crawl", fields: [
      { name: "Metrics.Crawl.Latest.Data.Status", label: "Crawl Status", component: CrawlStatus, 
        options: {
          adornment: 'end'
      }},
      { name: "Metrics.Crawl.Latest.Data", label: "Message", formatter: fmtCrawlMessage },
      { name: "Metrics.Crawl.Latest.Data.Completed", label: "Last Crawl", formatter: fmtIso8601AsLocalDateTime },
      { name: "Metrics.Crawl.Latest.Data.RunTimeSecs", label: "Run Time", formatter: fmtDaysHrsMinFromSecs },
    ]});
    fields.push({ section: "Schema", 
      expandable: true, panelFullWidth: true,
      expanded: schemaExpanded, expand: expandSchema, onExpandClick: handleExpandClick,
      footer: { 
        component: DatasetTables,
        props: { resourceId: resource.ResourceId },
      }
    });
  }

  return (
    <React.Fragment>
      <ResourceCard 
        resource={resource} 
        fields={fields} 
        stack 
        dashboard 
        bucket={resource.Location.S3Uri} 
        actions={actions}
      />
      {(openCrawlManager && crawlAction)?
        crawlAction==='Start'?
          <DatasetActionStartCrawl dataset={resource} onClose={() => setOpenCrawlManager(false)}/>
          :
          <DatasetActionStopCrawl dataset={resource} onClose={() => setOpenCrawlManager(false)}/>
        :null
      }
    </React.Fragment>
  );
}

DatasetCard.propTypes = {
  resource: PropTypes.object.isRequired,
};

DatasetCard.defaultProps = {
};
